import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Search,
  Table2,
  MoreHorizontal,
  Pencil,
  Trash2,
  Upload,
  Download,
  FileSpreadsheet,
  Eye,
  Settings2,
  X,
  Check,
  AlertCircle,
  Loader2,
  FilePlus2,
  Filter,
  Calculator,
  Copy,
} from "lucide-react";
import { toast } from "sonner";
import Papa from "papaparse";

import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  listReferenceTables,
  createReferenceTable,
  updateReferenceTable,
  deleteReferenceTable,
  duplicateReferenceTable,
  bulkDeleteReferenceTables,
  importReferenceData,
  getReferenceTablesUsageBySimulator,
  getReferenceTablesUsageCounts,
  type SimulatorUsageInfo,
} from "@/lib/api/references";
import { getErrorMessage } from "@/lib/api/client";
import { formatDate, slugify } from "@/lib/utils";
import { AdminGuard } from "@/lib/auth/AdminGuard";
import type { ReferenceTable, ReferenceTableWithVersions, ColumnDefinition } from "@/types";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";

// Type pour un fichier CSV parsé prêt à être importé
interface ParsedCSVFile {
  id: string;
  fileName: string;
  name: string;
  slug: string;
  description: string;
  columns: ColumnDefinition[];
  data: Record<string, unknown>[];
  error?: string;
  status: "pending" | "creating" | "success" | "error";
}

// Champ technique des grilles (références aux cellules Excel). L'export CSV l'écrit
// « [object Object] » : il n'a rien à faire dans un import.
const CELL_REFS_FIELD = "_cell_refs";

// Un nombre écrit en texte, à la française ou non : « 4,68 », « 1 234,5 », « 12 € ».
// C'est l'API qui convertit (services/import_grilles.py) ; l'écran n'annonce que le type.
const NOMBRE_EN_TEXTE = /^[-+]?(?:0|[1-9]\d*)(?:[.,]\d+)?$/;
const estUnNombre = (valeur: unknown) =>
  NOMBRE_EN_TEXTE.test(String(valeur).replace(/\s/g, "").replace(/€$/, ""));

const BOOLEENS_EN_TEXTE = ["true", "false", "vrai", "faux", "oui", "non", "yes", "no"];

// Détection automatique du type de colonne
function inferColumnType(values: unknown[]): "string" | "number" | "boolean" {
  const sampleValues = values.slice(0, 100).filter((v) => v !== null && v !== undefined && v !== "");

  if (sampleValues.length === 0) return "string";

  // Un nombre d'abord : une colonne de 0 et de 1 est une colonne de nombres.
  if (sampleValues.every(estUnNombre)) return "number";

  if (sampleValues.every((v) => BOOLEENS_EN_TEXTE.includes(String(v).trim().toLowerCase()))) {
    return "boolean";
  }

  return "string";
}

type CsvLu =
  | { columns: ColumnDefinition[]; data: Record<string, unknown>[]; error?: undefined }
  | { error: string };

// Colonnes et lignes d'un CSV lu par PapaParse, ou la raison de le refuser.
function lireCsv(lignes: Record<string, unknown>[]): CsvLu {
  if (lignes.length === 0) return { error: "Le fichier est vide" };

  const noms = Object.keys(lignes[0]).filter(
    (nom) => nom.trim() !== "" && nom !== CELL_REFS_FIELD
  );
  if (noms.length === 0) return { error: "Le fichier ne contient aucune colonne nommée" };

  const data = lignes.map((ligne) => Object.fromEntries(noms.map((nom) => [nom, ligne[nom]])));

  // Un fichier qui n'est pas en UTF-8 est lu avec le caractère de remplacement U+FFFD à la
  // place des accents : les libellés ne correspondraient plus à ceux que cherchent les formules.
  const malEncode = (valeur: unknown) => typeof valeur === "string" && valeur.includes("\uFFFD");
  if (noms.some(malEncode) || data.some((ligne) => Object.values(ligne).some(malEncode))) {
    return {
      error:
        "Le fichier n'est pas encodé en UTF-8. Dans Excel : Enregistrer sous → « CSV UTF-8 ».",
    };
  }

  const columns: ColumnDefinition[] = noms.map((name) => ({
    name,
    type: inferColumnType(data.map((ligne) => ligne[name])),
    required: false,
  }));

  return { columns, data };
}

export const Route = createFileRoute('/_dashboard/admin/references/')({
  component: AdminReferencesPage,
})

function AdminReferencesPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [selectedTable, setSelectedTable] = useState<ReferenceTable | null>(null);
  const [selectedTableIds, setSelectedTableIds] = useState<Set<number>>(new Set());
  const [selectedSimulatorIds, setSelectedSimulatorIds] = useState<number[]>([]);
  const [simulatorFilterOpen, setSimulatorFilterOpen] = useState(false);

  // État pour l'import CSV pour créer de nouvelles tables
  const [csvImportDialogOpen, setCsvImportDialogOpen] = useState(false);
  const [parsedFiles, setParsedFiles] = useState<ParsedCSVFile[]>([]);
  const [isImporting, setIsImporting] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
  });
  const [importData, setImportData] = useState<{
    columns: ColumnDefinition[];
    data: Record<string, unknown>[];
  } | null>(null);

  const queryClient = useQueryClient();
  const pageSize = 10;

  // Récupérer les simulateurs avec leur usage de tables
  const { data: simulatorsUsage } = useQuery({
    queryKey: ["references-usage-by-simulator"],
    queryFn: getReferenceTablesUsageBySimulator,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["references", { page, page_size: pageSize, search, simulator_ids: selectedSimulatorIds.join(",") }],
    queryFn: () =>
      listReferenceTables({
        page,
        page_size: pageSize,
        search: search || undefined,
        simulator_ids: selectedSimulatorIds.length > 0 ? selectedSimulatorIds.join(",") : undefined,
      }),
  });

  // Fetch usage counts for all tables
  const { data: usageCounts } = useQuery({
    queryKey: ["references-usage-counts"],
    queryFn: getReferenceTablesUsageCounts,
  });

  // Helper to get usage count for a table
  const getUsageCount = (tableId: number) => {
    const counts = usageCounts?.usage_counts?.[tableId];
    return counts ? counts.input_fields + counts.output_fields : 0;
  };

  const getUsageDetails = (tableId: number) => {
    const counts = usageCounts?.usage_counts?.[tableId];
    return counts || { input_fields: 0, output_fields: 0 };
  };

  const handleSimulatorToggle = (simulatorId: number) => {
    setSelectedSimulatorIds((prev) =>
      prev.includes(simulatorId)
        ? prev.filter((id) => id !== simulatorId)
        : [...prev, simulatorId]
    );
    setPage(1);
  };

  const clearSimulatorFilter = () => {
    setSelectedSimulatorIds([]);
    setPage(1);
  };

  const createMutation = useMutation({
    mutationFn: (data: { name: string; slug: string; description?: string }) =>
      createReferenceTable({
        ...data,
        columns: [{ name: "value", type: "string" }],
        data: [],
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["references"] });
      toast.success("Table créée");
      setCreateDialogOpen(false);
      resetForm();
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<ReferenceTable> }) =>
      updateReferenceTable(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["references"] });
      toast.success("Table mise à jour");
      setEditDialogOpen(false);
      resetForm();
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteReferenceTable(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["references"] });
      toast.success("Table supprimée");
      if (result.nullified_references) {
        toast.warning("Références mises à jour", {
          description: result.nullified_references.warning,
          duration: 8000,
        });
      }
      setDeleteDialogOpen(false);
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: number[]) => bulkDeleteReferenceTables(ids),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["references"] });
      toast.success(`${result.deleted_count} table${result.deleted_count > 1 ? "s" : ""} supprimée${result.deleted_count > 1 ? "s" : ""}`);
      if (result.errors && result.errors.length > 0) {
        toast.warning(`${result.errors.length} erreur${result.errors.length > 1 ? "s" : ""}`, {
          description: result.errors.join(", "),
        });
      }
      if (result.nullified_references) {
        toast.warning("Références mises à jour", {
          description: result.nullified_references.warning,
          duration: 8000,
        });
      }
      setBulkDeleteDialogOpen(false);
      setSelectedTableIds(new Set());
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const duplicateMutation = useMutation({
    mutationFn: (id: number) => duplicateReferenceTable(id),
    onSuccess: (newTable) => {
      queryClient.invalidateQueries({ queryKey: ["references"] });
      toast.success(`Table "${newTable.name}" créée`);
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const importMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: typeof importData }) =>
      importReferenceData(id, {
        columns: data!.columns,
        data: data!.data,
        replace_existing: true,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["references"] });
      toast.success("Données importées");
      setImportDialogOpen(false);
      setImportData(null);
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const resetForm = () => {
    setFormData({ name: "", slug: "", description: "" });
    setSelectedTable(null);
  };

  const handleNameChange = (name: string) => {
    setFormData({ ...formData, name, slug: slugify(name) });
  };

  const handleEdit = (table: ReferenceTable) => {
    setSelectedTable(table);
    setFormData({
      name: table.name,
      slug: table.slug,
      description: table.description || "",
    });
    setEditDialogOpen(true);
  };

  const handleView = (table: ReferenceTable) => {
    setSelectedTable(table);
    setViewDialogOpen(true);
  };

  const handleImport = (table: ReferenceTable) => {
    setSelectedTable(table);
    setImportDialogOpen(true);
  };

  const handleDelete = (table: ReferenceTable) => {
    setSelectedTable(table);
    setDeleteDialogOpen(true);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: "greedy",
      complete: (results) => {
        const csv = lireCsv(results.data as Record<string, unknown>[]);
        if (csv.error !== undefined) {
          toast.error(csv.error);
          setImportData(null);
          return;
        }

        setImportData({ columns: csv.columns, data: csv.data });
      },
      error: () => {
        toast.error("Erreur lors de la lecture du fichier");
      },
    });
  };

  // Fonction pour extraire le nom du fichier sans extension
  const getFileNameWithoutExtension = (fileName: string) => {
    return fileName.replace(/\.[^/.]+$/, "");
  };

  // Gestion de l'upload de plusieurs fichiers CSV
  const handleMultipleCSVUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    // Copie de la sélection : réinitialiser le champ, plus bas, vide la liste d'origine,
    // et la lecture des fichiers ne se termine qu'après.
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;

    const newParsedFiles: ParsedCSVFile[] = [];
    let filesProcessed = 0;

    files.forEach((file) => {
      const fileName = file.name;
      const baseName = getFileNameWithoutExtension(fileName);

      Papa.parse(file, {
        header: true,
        skipEmptyLines: "greedy",
        complete: (results) => {
          filesProcessed++;

          const csv = lireCsv(results.data as Record<string, unknown>[]);
          if (csv.error !== undefined) {
            newParsedFiles.push({
              id: crypto.randomUUID(),
              fileName,
              name: baseName,
              slug: slugify(baseName),
              description: "",
              columns: [],
              data: [],
              error: csv.error,
              status: "error",
            });
          } else {
            newParsedFiles.push({
              id: crypto.randomUUID(),
              fileName,
              name: baseName,
              slug: slugify(baseName),
              description: "",
              columns: csv.columns,
              data: csv.data,
              status: "pending",
            });
          }

          // Quand tous les fichiers sont traités
          if (filesProcessed === files.length) {
            setParsedFiles((prev) => [...prev, ...newParsedFiles]);
          }
        },
        error: () => {
          filesProcessed++;
          newParsedFiles.push({
            id: crypto.randomUUID(),
            fileName,
            name: baseName,
            slug: slugify(baseName),
            description: "",
            columns: [],
            data: [],
            error: "Erreur lors de la lecture du fichier",
            status: "error",
          });

          if (filesProcessed === files.length) {
            setParsedFiles((prev) => [...prev, ...newParsedFiles]);
          }
        },
      });
    });

    // Reset l'input pour permettre de resélectionner les mêmes fichiers
    event.target.value = "";
  }, []);

  // Mettre à jour un fichier parsé
  const updateParsedFile = (id: string, updates: Partial<ParsedCSVFile>) => {
    setParsedFiles((prev) =>
      prev.map((file) =>
        file.id === id ? { ...file, ...updates } : file
      )
    );
  };

  // Supprimer un fichier parsé
  const removeParsedFile = (id: string) => {
    setParsedFiles((prev) => prev.filter((file) => file.id !== id));
  };

  // Créer toutes les tables à partir des fichiers parsés
  const handleCreateAllTables = async () => {
    const validFiles = parsedFiles.filter((f) => f.status === "pending" && !f.error);
    
    if (validFiles.length === 0) {
      toast.error("Aucun fichier valide à importer");
      return;
    }

    setIsImporting(true);
    let successCount = 0;
    let errorCount = 0;

    for (const file of validFiles) {
      updateParsedFile(file.id, { status: "creating" });
      
      try {
        await createReferenceTable({
          name: file.name,
          slug: file.slug,
          description: file.description || undefined,
          columns: file.columns,
          data: file.data,
        });
        
        updateParsedFile(file.id, { status: "success" });
        successCount++;
      } catch (error) {
        updateParsedFile(file.id, { 
          status: "error", 
          error: getErrorMessage(error) 
        });
        errorCount++;
      }
    }

    setIsImporting(false);
    queryClient.invalidateQueries({ queryKey: ["references"] });

    if (successCount > 0) {
      toast.success(`${successCount} table${successCount > 1 ? "s" : ""} créée${successCount > 1 ? "s" : ""}`);
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} erreur${errorCount > 1 ? "s" : ""} lors de l'import`);
    }

    // Fermer le dialogue si tout est OK
    if (errorCount === 0) {
      setCsvImportDialogOpen(false);
      setParsedFiles([]);
    }
  };

  // Fermer et reset le dialogue d'import CSV
  const handleCloseCsvImport = () => {
    setCsvImportDialogOpen(false);
    setParsedFiles([]);
  };

  const handleExport = (table: ReferenceTable) => {
    if (!table.data || table.data.length === 0) {
      toast.error("Aucune donnée à exporter");
      return;
    }

    const csv = Papa.unparse(table.data);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${table.slug}.csv`;
    link.click();
  };

  // Gestion de la sélection
  const handleSelectAll = () => {
    if (!data?.items) return;
    
    const allCurrentPageIds = data.items.map((t) => t.id);
    const allSelected = allCurrentPageIds.every((id) => selectedTableIds.has(id));
    
    if (allSelected) {
      // Désélectionner tous les éléments de la page courante
      setSelectedTableIds((prev) => {
        const newSet = new Set(prev);
        allCurrentPageIds.forEach((id) => newSet.delete(id));
        return newSet;
      });
    } else {
      // Sélectionner tous les éléments de la page courante
      setSelectedTableIds((prev) => {
        const newSet = new Set(prev);
        allCurrentPageIds.forEach((id) => newSet.add(id));
        return newSet;
      });
    }
  };

  const handleSelectOne = (tableId: number) => {
    setSelectedTableIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(tableId)) {
        newSet.delete(tableId);
      } else {
        newSet.add(tableId);
      }
      return newSet;
    });
  };

  const clearSelection = () => {
    setSelectedTableIds(new Set());
  };

  const isAllCurrentPageSelected = data?.items 
    ? data.items.length > 0 && data.items.every((t) => selectedTableIds.has(t.id))
    : false;
  
  const isSomeCurrentPageSelected = data?.items
    ? data.items.some((t) => selectedTableIds.has(t.id)) && !isAllCurrentPageSelected
    : false;

  return (
    <AdminGuard>
    <div className="flex flex-col h-full">
      <Header title="Tables de référence" />

      <div className="flex-1 p-6 space-y-6 overflow-auto">
        {/* Actions bar */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-1 gap-4 w-full sm:w-auto">
            <div className="relative flex-1 sm:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher une table..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-9"
              />
            </div>
            {/* Filtre par simulateur */}
            <Popover open={simulatorFilterOpen} onOpenChange={setSimulatorFilterOpen}>
              <PopoverTrigger asChild>
                <Button 
                  variant={selectedSimulatorIds.length > 0 ? "default" : "outline"} 
                  className="gap-2 shrink-0"
                >
                  <Filter className="h-4 w-4" />
                  {selectedSimulatorIds.length > 0 ? (
                    <span className="flex items-center gap-1">
                      <span className="hidden sm:inline">Simulateur</span>
                      <Badge variant="secondary" className="rounded-full px-2 py-0 text-xs">
                        {selectedSimulatorIds.length}
                      </Badge>
                    </span>
                  ) : (
                    <span className="hidden sm:inline">Filtrer par simulateur</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="start">
                <div className="p-3 border-b">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-sm">Filtrer par simulateur</h4>
                    {selectedSimulatorIds.length > 0 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={clearSimulatorFilter}
                        className="h-7 px-2 text-muted-foreground hover:text-foreground"
                      >
                        Effacer
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Afficher uniquement les tables utilisées par les simulateurs sélectionnés
                  </p>
                </div>
                <ScrollArea className="max-h-[300px]">
                  <div className="p-2 space-y-1">
                    {simulatorsUsage?.simulators.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        Aucun simulateur trouvé
                      </p>
                    ) : (
                      simulatorsUsage?.simulators.map((sim) => (
                        <label
                          key={sim.id}
                          className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-muted/50 cursor-pointer transition-colors"
                        >
                          <Checkbox
                            checked={selectedSimulatorIds.includes(sim.id)}
                            onCheckedChange={() => handleSimulatorToggle(sim.id)}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Calculator className="h-4 w-4 text-muted-foreground shrink-0" />
                              <span className="text-sm font-medium truncate">{sim.name}</span>
                            </div>
                          </div>
                          <Badge variant="outline" className="shrink-0 text-xs">
                            {sim.reference_table_count} table{sim.reference_table_count !== 1 ? "s" : ""}
                          </Badge>
                        </label>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setCsvImportDialogOpen(true)}>
              <FilePlus2 className="mr-2 h-4 w-4" />
              Importer CSV
            </Button>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Nouvelle table
            </Button>
          </div>
        </div>

        {/* Active filter indicator */}
        {selectedSimulatorIds.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-muted-foreground">Filtré par :</span>
            {simulatorsUsage?.simulators
              .filter((s) => selectedSimulatorIds.includes(s.id))
              .map((sim) => (
                <Badge 
                  key={sim.id} 
                  variant="secondary" 
                  className="gap-1 pr-1"
                >
                  <Calculator className="h-3 w-3" />
                  {sim.name}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-4 w-4 p-0 hover:bg-transparent"
                    onClick={() => handleSimulatorToggle(sim.id)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              ))}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={clearSimulatorFilter}
              className="h-6 px-2 text-xs text-muted-foreground"
            >
              Tout effacer
            </Button>
          </div>
        )}

        {/* Barre d'action de sélection */}
        <AnimatePresence>
          {selectedTableIds.size > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="fixed top-20 left-1/2 -translate-x-1/2 z-50"
            >
              <Card className="shadow-lg border-primary/20 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
                <CardContent className="flex items-center gap-4 py-3 px-4">
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-sm font-semibold text-primary">
                        {selectedTableIds.size}
                      </span>
                    </div>
                    <span className="text-sm font-medium">
                      table{selectedTableIds.size > 1 ? "s" : ""} sélectionnée{selectedTableIds.size > 1 ? "s" : ""}
                    </span>
                  </div>
                  <div className="h-4 w-px bg-border" />
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setBulkDeleteDialogOpen(true)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Supprimer
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearSelection}
                  >
                    <X className="mr-2 h-4 w-4" />
                    Annuler
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">
                    <Checkbox
                      checked={isAllCurrentPageSelected}
                      ref={(el) => {
                        if (el) {
                          (el as unknown as HTMLInputElement).indeterminate = isSomeCurrentPageSelected;
                        }
                      }}
                      onCheckedChange={handleSelectAll}
                      aria-label="Sélectionner tout"
                    />
                  </TableHead>
                  <TableHead>Nom</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Colonnes</TableHead>
                  <TableHead>Lignes</TableHead>
                  <TableHead>Versions liées</TableHead>
                  <TableHead>Mis à jour</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  [...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Skeleton className="h-4 w-4" />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Skeleton className="h-10 w-10 rounded-lg" />
                          <div className="space-y-2">
                            <Skeleton className="h-4 w-32" />
                            <Skeleton className="h-3 w-48" />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                      <TableCell><Skeleton className="h-8 w-8" /></TableCell>
                    </TableRow>
                  ))
                ) : data?.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="h-32 text-center">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <Table2 className="h-10 w-10 opacity-50" />
                        <p>Aucune table de référence</p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCreateDialogOpen(true)}
                        >
                          Créer une table
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  data?.items.map((table, index) => (
                    <TableRow
                      key={table.id}
                      className={`border-b transition-colors hover:bg-muted/50 ${
                        selectedTableIds.has(table.id) ? "bg-primary/5" : ""
                      }`}
                      style={{
                        animation: `fadeInUp 0.3s ease-out ${index * 0.05}s both`,
                      }}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedTableIds.has(table.id)}
                          onCheckedChange={() => handleSelectOne(table.id)}
                          aria-label={`Sélectionner ${table.name}`}
                        />
                      </TableCell>
                      <TableCell className="p-0">
                        <Link
                          to="/admin/references/$id"
                          params={{ id: String(table.id) }}
                          className="flex items-center gap-3 hover:text-primary transition-colors text-left p-4 w-full"
                        >
                          <div className="h-10 w-10 rounded-lg bg-info/10 flex items-center justify-center shrink-0">
                            <Table2 className="h-5 w-5 text-info" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium">{table.name}</p>
                            {table.description && (
                              <p className="text-sm text-muted-foreground line-clamp-1 max-w-xs">
                                {table.description}
                              </p>
                            )}
                          </div>
                        </Link>
                      </TableCell>
                      <TableCell>
                        <code className="px-2 py-1 bg-muted rounded text-xs">
                          {table.slug}
                        </code>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{table.columns?.length || 0}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{table.data?.length || 0}</span>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const versions = (table as ReferenceTableWithVersions).linked_versions ?? [];
                          if (versions.length === 0) {
                            return (
                              <span className="text-sm text-muted-foreground">—</span>
                            );
                          }
                          const grouped = versions.reduce<Record<string, number[]>>((acc, v) => {
                            if (!acc[v.simulator_name]) acc[v.simulator_name] = [];
                            acc[v.simulator_name].push(v.version_number);
                            return acc;
                          }, {});
                          const summary = Object.entries(grouped)
                            .map(([name, nums]) => `${name} v${nums.sort((a, b) => a - b).join(", v")}`)
                            .join(" • ");
                          return (
                            <div className="flex items-center gap-1.5 max-w-[200px]">
                              <Badge variant="secondary" className="text-xs px-2 shrink-0">
                                {versions.length}
                              </Badge>
                              <span className="text-xs text-muted-foreground truncate" title={summary}>
                                {summary}
                              </span>
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(table.updated_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleView(table)}
                            title="Aperçu rapide"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem asChild>
                                <Link to="/admin/references/$id" params={{ id: String(table.id) }}>
                                  <Settings2 className="mr-2 h-4 w-4" />
                                  Configurer
                                </Link>
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleImport(table)}>
                                <Upload className="mr-2 h-4 w-4" />
                                Importer CSV
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleExport(table)}>
                                <Download className="mr-2 h-4 w-4" />
                                Exporter CSV
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => duplicateMutation.mutate(table.id)}>
                                <Copy className="mr-2 h-4 w-4" />
                                Dupliquer
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleEdit(table)}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Renommer
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDelete(table)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Pagination */}
        {data && data.total_pages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {data.total_items} table{data.total_items > 1 ? "s" : ""} au total
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Précédent
              </Button>
              <div className="flex items-center gap-1 px-3 py-1 bg-muted rounded text-sm">
                {page} / {data.total_pages}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))}
                disabled={page === data.total_pages}
              >
                Suivant
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle table de référence</DialogTitle>
            <DialogDescription>
              Créez une table pour stocker des données de référence globales
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Tarifs zones"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="slug">Slug</Label>
              <Input
                id="slug"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="tarifs-zones"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={() => createMutation.mutate(formData)}
              disabled={createMutation.isPending || !formData.name || !formData.slug}
            >
              {createMutation.isPending ? "Création..." : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier la table</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Nom</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={() =>
                selectedTable &&
                updateMutation.mutate({
                  id: selectedTable.id,
                  data: { name: formData.name, description: formData.description },
                })
              }
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Importer des données CSV</DialogTitle>
            <DialogDescription>
              Les données existantes seront remplacées
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <FileSpreadsheet className="h-10 w-10 mx-auto mb-4 text-muted-foreground" />
              <Label htmlFor="csv-file" className="cursor-pointer">
                <span className="text-primary hover:underline">Cliquez pour sélectionner</span>
                <span className="text-muted-foreground"> ou glissez un fichier CSV</span>
              </Label>
              <Input
                id="csv-file"
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
              />
            </div>
            {importData && (
              <div className="p-3 rounded-lg bg-muted">
                <p className="text-sm font-medium">
                  {importData.columns.length} colonnes, {importData.data.length} lignes
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Colonnes: {importData.columns.map((c) => c.name).join(", ")}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={() =>
                selectedTable &&
                importData &&
                importMutation.mutate({ id: selectedTable.id, data: importData })
              }
              disabled={importMutation.isPending || !importData}
            >
              {importMutation.isPending ? "Import..." : "Importer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{selectedTable?.name}</DialogTitle>
            <DialogDescription>
              {selectedTable?.data?.length || 0} lignes • {selectedTable?.columns?.length || 0} colonnes
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            {selectedTable?.data && selectedTable.data.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    {selectedTable.columns?.map((col) => (
                      <TableHead key={col.name}>{col.name}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedTable.data.slice(0, 100).map((row, i) => (
                    <TableRow key={i}>
                      {selectedTable.columns?.map((col) => (
                        <TableCell key={col.name}>
                          {String(row[col.name] ?? "")}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-center py-8 text-muted-foreground">
                Aucune donnée
              </p>
            )}
            {selectedTable?.data && selectedTable.data.length > 100 && (
              <p className="text-center py-4 text-sm text-muted-foreground">
                Affichage limité aux 100 premières lignes
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer la table</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer &quot;{selectedTable?.name}&quot; ?
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={() => selectedTable && deleteMutation.mutate(selectedTable.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Suppression..." : "Supprimer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              Supprimer {selectedTableIds.size} table{selectedTableIds.size > 1 ? "s" : ""}
            </DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer ces {selectedTableIds.size} table{selectedTableIds.size > 1 ? "s" : ""} de référence ?
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="max-h-[200px] overflow-auto rounded-lg border bg-muted/50 p-3">
              <ul className="space-y-1">
                {data?.items
                  .filter((t) => selectedTableIds.has(t.id))
                  .map((table) => (
                    <li key={table.id} className="flex items-center gap-2 text-sm">
                      <Table2 className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="font-medium">{table.name}</span>
                      <code className="text-xs text-muted-foreground">({table.slug})</code>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBulkDeleteDialogOpen(false)}
              disabled={bulkDeleteMutation.isPending}
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={() => bulkDeleteMutation.mutate(Array.from(selectedTableIds))}
              disabled={bulkDeleteMutation.isPending}
            >
              {bulkDeleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Supprimer {selectedTableIds.size} table{selectedTableIds.size > 1 ? "s" : ""}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Import Dialog - Créer des tables à partir de fichiers CSV */}
      <Dialog open={csvImportDialogOpen} onOpenChange={handleCloseCsvImport}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FilePlus2 className="h-5 w-5 text-primary" />
              Importer des fichiers CSV
            </DialogTitle>
            <DialogDescription>
              Sélectionnez un ou plusieurs fichiers CSV pour créer automatiquement des tables de référence
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-hidden flex flex-col gap-4">
            {/* Zone de drop / sélection de fichiers */}
            <div className="border-2 border-dashed rounded-xl p-6 text-center hover:border-primary/50 hover:bg-primary/5 transition-colors">
              <FileSpreadsheet className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
              <Label htmlFor="csv-files-input" className="cursor-pointer">
                <span className="text-primary font-medium hover:underline">
                  Cliquez pour sélectionner
                </span>
                <span className="text-muted-foreground"> ou glissez vos fichiers CSV</span>
              </Label>
              <p className="text-xs text-muted-foreground mt-2">
                Vous pouvez sélectionner plusieurs fichiers à la fois
              </p>
              <Input
                id="csv-files-input"
                type="file"
                accept=".csv"
                multiple
                onChange={handleMultipleCSVUpload}
                className="hidden"
              />
            </div>

            {/* Liste des fichiers parsés */}
            {parsedFiles.length > 0 && (
              <ScrollArea className="flex-1 -mx-6 px-6">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">
                      {parsedFiles.length} fichier{parsedFiles.length > 1 ? "s" : ""} sélectionné{parsedFiles.length > 1 ? "s" : ""}
                    </p>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setParsedFiles([])}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      Tout effacer
                    </Button>
                  </div>

                  <Accordion type="multiple" className="space-y-2">
                    <AnimatePresence>
                      {parsedFiles.map((file) => (
                        <motion.div
                          key={file.id}
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                        >
                          <AccordionItem 
                            value={file.id} 
                            className={`border rounded-lg px-4 ${
                              file.status === "error" 
                                ? "border-destructive/50 bg-destructive/5" 
                                : file.status === "success"
                                ? "border-success/50 bg-success/5"
                                : ""
                            }`}
                          >
                            <AccordionTrigger className="hover:no-underline py-3">
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                {/* Icône de statut */}
                                {file.status === "creating" ? (
                                  <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" />
                                ) : file.status === "success" ? (
                                  <Check className="h-4 w-4 text-success shrink-0" />
                                ) : file.status === "error" ? (
                                  <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
                                ) : (
                                  <FileSpreadsheet className="h-4 w-4 text-muted-foreground shrink-0" />
                                )}

                                <div className="flex-1 min-w-0 text-left">
                                  <p className="font-medium truncate">{file.name}</p>
                                  <p className="text-xs text-muted-foreground truncate">
                                    {file.fileName} • {file.columns.length} colonnes • {file.data.length} lignes
                                  </p>
                                </div>

                                {/* Badges de statut */}
                                {file.status === "success" && (
                                  <Badge variant="default" className="bg-success shrink-0">
                                    Créée
                                  </Badge>
                                )}
                                {file.status === "error" && (
                                  <Badge variant="destructive" className="shrink-0">
                                    Erreur
                                  </Badge>
                                )}

                                {/* Bouton supprimer */}
                                {file.status === "pending" && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 shrink-0"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeParsedFile(file.id);
                                    }}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="pb-4">
                              {file.error ? (
                                <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                                  {file.error}
                                </div>
                              ) : (
                                <div className="space-y-4">
                                  {/* Édition du nom et slug */}
                                  <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                      <Label htmlFor={`name-${file.id}`}>Nom de la table</Label>
                                      <Input
                                        id={`name-${file.id}`}
                                        value={file.name}
                                        onChange={(e) => {
                                          const newName = e.target.value;
                                          updateParsedFile(file.id, {
                                            name: newName,
                                            slug: slugify(newName),
                                          });
                                        }}
                                        disabled={file.status !== "pending"}
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label htmlFor={`slug-${file.id}`}>Slug</Label>
                                      <Input
                                        id={`slug-${file.id}`}
                                        value={file.slug}
                                        onChange={(e) => updateParsedFile(file.id, { slug: e.target.value })}
                                        disabled={file.status !== "pending"}
                                      />
                                    </div>
                                  </div>

                                  {/* Description */}
                                  <div className="space-y-2">
                                    <Label htmlFor={`desc-${file.id}`}>Description (optionnel)</Label>
                                    <Textarea
                                      id={`desc-${file.id}`}
                                      value={file.description}
                                      onChange={(e) => updateParsedFile(file.id, { description: e.target.value })}
                                      placeholder="Description de la table..."
                                      rows={2}
                                      disabled={file.status !== "pending"}
                                    />
                                  </div>

                                  {/* Aperçu des colonnes */}
                                  <div className="space-y-2">
                                    <Label>Colonnes détectées</Label>
                                    <div className="flex flex-wrap gap-2">
                                      {file.columns.map((col) => (
                                        <div 
                                          key={col.name}
                                          className="flex items-center gap-1 px-2 py-1 bg-muted rounded-md text-sm"
                                        >
                                          <span className="font-medium">{col.name}</span>
                                          <Badge variant="outline" className="text-xs">
                                            {col.type}
                                          </Badge>
                                        </div>
                                      ))}
                                    </div>
                                  </div>

                                  {/* Aperçu des données */}
                                  <div className="space-y-2">
                                    <Label>Aperçu des données (5 premières lignes)</Label>
                                    <div className="border rounded-lg overflow-hidden">
                                      <div className="overflow-x-auto">
                                        <Table>
                                          <TableHeader>
                                            <TableRow>
                                              {file.columns.map((col) => (
                                                <TableHead key={col.name} className="text-xs whitespace-nowrap">
                                                  {col.name}
                                                </TableHead>
                                              ))}
                                            </TableRow>
                                          </TableHeader>
                                          <TableBody>
                                            {file.data.slice(0, 5).map((row, idx) => (
                                              <TableRow key={idx}>
                                                {file.columns.map((col) => (
                                                  <TableCell key={col.name} className="text-xs">
                                                    {String(row[col.name] ?? "")}
                                                  </TableCell>
                                                ))}
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </div>
                                    </div>
                                    {file.data.length > 5 && (
                                      <p className="text-xs text-muted-foreground">
                                        + {file.data.length - 5} autres lignes
                                      </p>
                                    )}
                                  </div>
                                </div>
                              )}
                            </AccordionContent>
                          </AccordionItem>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </Accordion>
                </div>
              </ScrollArea>
            )}
          </div>

          <DialogFooter className="flex-shrink-0 gap-2 sm:gap-0">
            <Button variant="outline" onClick={handleCloseCsvImport} disabled={isImporting}>
              Annuler
            </Button>
            <Button
              onClick={handleCreateAllTables}
              disabled={
                isImporting || 
                parsedFiles.length === 0 || 
                parsedFiles.filter((f) => f.status === "pending" && !f.error).length === 0
              }
            >
              {isImporting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Création en cours...
                </>
              ) : (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Créer {parsedFiles.filter((f) => f.status === "pending" && !f.error).length} table{parsedFiles.filter((f) => f.status === "pending" && !f.error).length > 1 ? "s" : ""}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </AdminGuard>
  );
}

